import { getRgbaColor, isNumber, isPointAbsent } from '@/graph/helper'
import { type GraphConfigInterface } from '@/graph/config'
import { defaultConfigValues, EXIT_DEFAULT_SIZE, EXIT_DEFAULT_COLOR_CHANNEL } from '@/graph/variables'

export enum PointShape {
  Circle = 0,
  Square = 1,
  Triangle = 2,
  Diamond = 3,
  Pentagon = 4,
  Hexagon = 5,
  Star = 6,
  Cross = 7,
  None = 8
}

/** Link stroke pattern; one value per link via `setLinkStyles`. Dash metrics come from `linkDashLength`/`linkDashGap`. */
export enum LinkStyle {
  Solid = 0,
  Dashed = 1,
  Dotted = 2
}

export class GraphData {
  public inputPointPositions: Float32Array | undefined
  public inputPointColors: Float32Array | undefined
  public inputPointSizes: Float32Array | undefined
  public inputPointShapes: Float32Array | undefined
  public inputImageData: ImageData[] | undefined
  public inputPointImageIndices: Float32Array | undefined
  public inputPointImageSizes: Float32Array | undefined
  public inputLinkColors: Float32Array | undefined
  public inputLinkWidths: Float32Array | undefined
  /** Raw per-link stroke patterns from `setLinkStyles` (`LinkStyle` per link); sanitized into `linkStyles`. */
  public inputLinkStyles: Float32Array | undefined
  public inputLinkStrength: Float32Array | undefined
  public inputPointClusters: (number | undefined)[] | undefined
  public inputClusterPositions: (number | undefined)[] | undefined
  public inputClusterStrength: Float32Array | undefined
  public inputPinnedPoints: number[] | undefined

  public pointPositions: Float32Array | undefined
  /**
   * Number of points before the latest data update.
   * Used as the `from` value for point transitions.
   * This lets transitions handle added or removed points correctly.
   */
  public sourcePointsNumber = 0
  /**
   * Number of points after the latest data update.
   * Used as the `to` value for point transitions.
   * This lets transitions handle added or removed points correctly.
   */
  public targetPointsNumber = 0
  public pointColors: Float32Array | undefined
  public pointSizes: Float32Array | undefined
  public pointShapes: Float32Array | undefined
  public pointImageIndices: Float32Array | undefined
  public pointImageSizes: Float32Array | undefined

  public inputLinks: Float32Array | undefined
  public links: Float32Array | undefined
  public linkColors: Float32Array | undefined
  public linkWidths: Float32Array | undefined
  /** Sanitized per-link stroke patterns (integer `LinkStyle` values) that the Lines module uploads. */
  public linkStyles: Float32Array | undefined
  public linkArrowsBoolean: boolean[] | undefined
  public linkArrows: number[] | undefined
  public linkStrength: Float32Array | undefined

  public pointClusters: (number | undefined)[] | undefined
  public clusterPositions: (number | undefined)[] | undefined
  public clusterStrength: Float32Array | undefined

  /**
   * Each inner array of `sourceIndexToTargetIndices` and `targetIndexToSourceIndices` contains pairs where:
   *   - The first value is the target/source index in the point array.
   *   - The second value is the link index in the array of links.
  */
  public sourceIndexToTargetIndices: ([number, number][] | undefined)[] | undefined
  public targetIndexToSourceIndices: ([number, number][] | undefined)[] | undefined

  public degree: number[] | undefined
  public inDegree: number[] | undefined
  public outDegree: number[] | undefined
  private _config: GraphConfigInterface
  /** Lazily parsed `pointDefaultColor` — see the `defaultRgba` getter. */
  private _defaultRgba: [number, number, number, number] | undefined

  public constructor (config: GraphConfigInterface) {
    this._config = config
  }

  public get pointsNumber (): number | undefined {
    return this.pointPositions && this.pointPositions.length / 2
  }

  public get linksNumber (): number | undefined {
    return this.links && this.links.length / 2
  }

  /**
   * Parsed `pointDefaultColor`, cached between updates (`updatePointColor`
   * invalidates it, so config changes are picked up). Public so the draw pass can
   * feed it to the shader's `pointDefaultColor` uniform without re-parsing the
   * config color string every frame.
   */
  public get defaultRgba (): [number, number, number, number] {
    this._defaultRgba ??= getRgbaColor(this._config.pointDefaultColor)
    return this._defaultRgba
  }

  public updatePoints (): void {
    // Positions must hold whole [x, y] pairs: an odd length makes `pointsNumber`
    // fractional, which the adjacency and degree builds pass to `new Array()`.
    // `subarray` is a view over the same buffer — the caller's array is not edited.
    if (this.inputPointPositions !== undefined && this.inputPointPositions.length % 2 !== 0) {
      console.warn(`Invalid point positions length: ${this.inputPointPositions.length}. The array must hold [x, y] pairs — the trailing value was ignored.`)
      this.inputPointPositions = this.inputPointPositions.subarray(0, this.inputPointPositions.length - 1)
    }

    // Don't sync the same positions twice — it breaks animations when points are added or removed.
    if (this.pointPositions === this.inputPointPositions) return

    this.sourcePointsNumber = this.pointPositions ? this.pointPositions.length / 2 : 0
    this.pointPositions = this.inputPointPositions
    this.targetPointsNumber = this.pointPositions ? this.pointPositions.length / 2 : 0
  }

  /**
   * Updates the point colors based on the input data or default config value.
   */
  public updatePointColor (): void {
    if (this.pointsNumber === undefined) {
      this.pointColors = undefined
      return
    }

    // NaN channels are not resolved here — the caller's array is used as-is (never
    // edited), and resolution happens at read time: the draw shader resolves NaN
    // against the point's absence and the config default, and CPU consumers go
    // through `getResolvedPointColorChannel`/`getResolvedPointSize`. A missing or
    // mismatched input becomes an all-NaN array: "resolve every channel".
    this._defaultRgba = undefined // config may have changed — re-parse lazily
    if (this.inputPointColors === undefined || this.inputPointColors.length / 4 !== this.pointsNumber) {
      this.pointColors = new Float32Array(this.pointsNumber * 4).fill(NaN)
    } else {
      this.pointColors = this.inputPointColors
    }
  }

  /**
   * Resolves one color channel of a point the way the draw shader does: a `NaN`
   * channel means the exit default (transparent) for an **absent** point, the
   * config default otherwise. The CPU mirror of the shader rule, for consumers
   * that read colors outside the GPU.
   */
  public getResolvedPointColorChannel (index: number, channel: number): number {
    const raw = this.pointColors?.[index * 4 + channel]
    if (isNumber(raw)) return raw as number
    if (this.pointPositions && isPointAbsent(this.pointPositions, index)) return EXIT_DEFAULT_COLOR_CHANNEL
    return this.defaultRgba[channel] as number
  }

  /**
   * Updates the point sizes based on the input data or default config value.
   */
  public updatePointSize (): void {
    if (this.pointsNumber === undefined) {
      this.pointSizes = undefined
      return
    }

    // Like updatePointColor: no resolution here — read-time resolution in the draw
    // shader / `getResolvedPointSize`, and the caller's array is never edited.
    if (this.inputPointSizes === undefined || this.inputPointSizes.length !== this.pointsNumber) {
      this.pointSizes = new Float32Array(this.pointsNumber).fill(NaN)
    } else {
      this.pointSizes = this.inputPointSizes
    }
  }

  /**
   * Resolves a point's size the way the draw shader does: `NaN` means the exit
   * default (`0`) for an **absent** point, the config default otherwise. The CPU
   * mirror of the shader rule, for consumers that read sizes outside the GPU
   * (collision, read-back, the image-size fallback).
   */
  public getResolvedPointSize (index: number): number {
    const raw = this.pointSizes?.[index]
    if (isNumber(raw)) return raw as number
    if (this.pointPositions && isPointAbsent(this.pointPositions, index)) return EXIT_DEFAULT_SIZE
    return this._config.pointDefaultSize
  }

  /**
   * Updates the point shapes based on the input data or default config value.
   * Images are rendered above shapes.
   */
  public updatePointShape (): void {
    if (this.pointsNumber === undefined) {
      this.pointShapes = undefined
      return
    }

    const { pointDefaultShape } = this._config
    const configShape = typeof pointDefaultShape === 'string' ? Number(pointDefaultShape) : pointDefaultShape
    // PointShape is an integer enum; the draw shader matches shapes by exact equality,
    // so a fractional value would silently render as the shader's fallback (circle)
    // instead of the configured default. Require integers — Number.isInteger also rejects NaN.
    const defaultShape = (Number.isInteger(configShape) && configShape >= 0 && configShape <= 8) ? configShape : defaultConfigValues.pointDefaultShape

    // Sets point shapes to default values if the input is missing or does not match input points number.
    if (this.inputPointShapes === undefined || this.inputPointShapes.length !== this.pointsNumber) {
      this.pointShapes = new Float32Array(this.pointsNumber).fill(defaultShape)
    } else {
      this.pointShapes = new Float32Array(this.inputPointShapes)
      const pointShapes = this.pointShapes
      for (let i = 0; i < pointShapes.length; i++) {
        // In-bounds Float32Array reads never yield undefined; `?? -1` narrows the type
        // and routes the impossible case into the range check.
        const shape = pointShapes[i] ?? -1
        if (!Number.isInteger(shape) || shape < 0 || shape > 8) {
          pointShapes[i] = defaultShape
        }
      }
    }
  }

  /**
   * Updates the point image indices based on the input data or default value (-1 for no image).
   */
  public updatePointImageIndices (): void {
    if (this.pointsNumber === undefined) {
      this.pointImageIndices = undefined
      return
    }

    // Sets point image indices to -1 if input is missing or doesn't match points count
    if (this.inputPointImageIndices === undefined || this.inputPointImageIndices.length !== this.pointsNumber) {
      this.pointImageIndices = new Float32Array(this.pointsNumber).fill(-1)
    } else {
      const pointImageIndices = new Float32Array(this.inputPointImageIndices)
      for (let i = 0; i < pointImageIndices.length; i++) {
        const rawIndex = pointImageIndices[i]
        const imageIndex = (rawIndex === undefined) ? NaN : rawIndex
        if (!Number.isFinite(imageIndex) || imageIndex < 0) {
          pointImageIndices[i] = -1
        } else {
          pointImageIndices[i] = Math.trunc(imageIndex)
        }
      }
      this.pointImageIndices = pointImageIndices
    }
  }

  /**
   * Updates the point image sizes based on the input data or default to point sizes.
   */
  public updatePointImageSizes (): void {
    if (this.pointsNumber === undefined) {
      this.pointImageSizes = undefined
      return
    }

    // Sets point image sizes to point sizes if the input is missing or does not match input points number.
    // Point sizes are read through the resolver: the raw array may hold NaN
    // ("use the default"), which must not reach the image-size buffer.
    if (this.inputPointImageSizes === undefined || this.inputPointImageSizes.length !== this.pointsNumber) {
      this.pointImageSizes = new Float32Array(this.pointsNumber)
      for (let i = 0; i < this.pointsNumber; i++) this.pointImageSizes[i] = this.getResolvedPointSize(i)
    } else {
      this.pointImageSizes = new Float32Array(this.inputPointImageSizes)
      for (let i = 0; i < this.pointImageSizes.length; i++) {
        if (!isNumber(this.pointImageSizes[i])) {
          this.pointImageSizes[i] = this.getResolvedPointSize(i)
        }
      }
    }
  }

  /**
   * Whether a point draws an atlas image: an image index in range of the images
   * set. Only then does its image size count toward its footprint — the size slot
   * of a point without an image still holds a value (the point size by default),
   * and that must not size the point, its rings or its hit box.
   */
  public pointDrawsImage (index: number): boolean {
    const imageIndex = this.pointImageIndices?.[index] ?? -1
    return imageIndex >= 0 && imageIndex < (this.inputImageData?.length ?? 0)
  }

  /**
   * The sprite's footprint: the resolved point size, or the image size when the
   * point draws an image and that is larger — the draw shader's `max(shape, image)`
   * for consumers outside the GPU (the rect-selection texture, `getPointRadiusByIndex`).
   * The ring shader applies the same rule on the GPU, to the mixed size.
   */
  public getResolvedPointFootprint (index: number): number {
    const imageSize = this.pointDrawsImage(index) ? this.pointImageSizes?.[index] : undefined
    return Math.max(this.getResolvedPointSize(index), imageSize ?? 0)
  }

  /**
   * True when `index` addresses a real point. Link endpoints come straight from
   * the caller, so they may be out of range, negative or fractional.
   */
  public isPointIndex (index: number | undefined): index is number {
    return index !== undefined && Number.isInteger(index) &&
      index >= 0 && index < (this.pointsNumber ?? 0)
  }

  public updateLinks (): void {
    // Links must hold whole [source, target] pairs: an odd length makes
    // `linksNumber` fractional, and `new Array(linksNumber)` in `updateArrows`
    // throws from inside the deferred render, leaving the graph blank with no
    // error surfaced. `subarray` is a view — the caller's array is not edited.
    if (this.inputLinks !== undefined && this.inputLinks.length % 2 !== 0) {
      console.warn(`Invalid links length: ${this.inputLinks.length}. The array must hold [source, target] pairs — the trailing value was ignored.`)
      this.inputLinks = this.inputLinks.subarray(0, this.inputLinks.length - 1)
    }

    this.links = this.inputLinks
  }

  /**
   * Updates the link colors based on the input data or default config value.
   */
  public updateLinkColor (): void {
    if (this.linksNumber === undefined) {
      this.linkColors = undefined
      return
    }

    // Sets link colors to default values from config if the input is missing or does not match input links number.
    const defaultRgba = getRgbaColor(this._config.linkDefaultColor)
    if (this.inputLinkColors === undefined || this.inputLinkColors.length / 4 !== this.linksNumber) {
      this.linkColors = new Float32Array(this.linksNumber * 4)

      for (let i = 0; i < this.linkColors.length / 4; i++) {
        this.linkColors[i * 4] = defaultRgba[0]
        this.linkColors[i * 4 + 1] = defaultRgba[1]
        this.linkColors[i * 4 + 2] = defaultRgba[2]
        this.linkColors[i * 4 + 3] = defaultRgba[3]
      }
    } else {
      this.linkColors = this.inputLinkColors
      for (let i = 0; i < this.linkColors.length / 4; i++) {
        if (!isNumber(this.linkColors[i * 4])) this.linkColors[i * 4] = defaultRgba[0]
        if (!isNumber(this.linkColors[i * 4 + 1])) this.linkColors[i * 4 + 1] = defaultRgba[1]
        if (!isNumber(this.linkColors[i * 4 + 2])) this.linkColors[i * 4 + 2] = defaultRgba[2]
        if (!isNumber(this.linkColors[i * 4 + 3])) this.linkColors[i * 4 + 3] = defaultRgba[3]
      }
    }
  }

  /**
   * Updates the link width based on the input data or default config value.
   */
  public updateLinkWidth (): void {
    if (this.linksNumber === undefined) {
      this.linkWidths = undefined
      return
    }

    // Sets link widths to default values from config if the input is missing or does not match input links number.
    const defaultWidth = this._config.linkDefaultWidth
    if (this.inputLinkWidths === undefined || this.inputLinkWidths.length !== this.linksNumber) {
      this.linkWidths = new Float32Array(this.linksNumber).fill(defaultWidth)
    } else {
      this.linkWidths = this.inputLinkWidths
      for (let i = 0; i < this.linkWidths.length; i++) {
        if (!isNumber(this.linkWidths[i])) {
          this.linkWidths[i] = defaultWidth
        }
      }
    }
  }

  /**
   * Updates the link styles (stroke patterns) based on the input data or default config value.
   */
  public updateLinkStyles (): void {
    if (this.linksNumber === undefined) {
      this.linkStyles = undefined
      return
    }

    const { linkDefaultStyle } = this._config
    const configStyle = typeof linkDefaultStyle === 'string' ? Number(linkDefaultStyle) : linkDefaultStyle
    // LinkStyle is an integer enum; the fragment shader picks the pattern with thresholds,
    // so a fractional value would silently select a pattern instead of the documented
    // fallback. Require integers — Number.isInteger also rejects NaN.
    const defaultStyle = (Number.isInteger(configStyle) && configStyle >= 0 && configStyle <= 2) ? configStyle : defaultConfigValues.linkDefaultStyle

    // Sets link styles to default values if the input is missing or does not match input links number.
    if (this.inputLinkStyles === undefined || this.inputLinkStyles.length !== this.linksNumber) {
      this.linkStyles = new Float32Array(this.linksNumber).fill(defaultStyle)
    } else {
      this.linkStyles = new Float32Array(this.inputLinkStyles)
      const linkStyles = this.linkStyles
      for (let i = 0; i < linkStyles.length; i++) {
        // See updatePointShape: `?? -1` narrows the indexed read for the type checker.
        const style = linkStyles[i] ?? -1
        if (!Number.isInteger(style) || style < 0 || style > 2) {
          linkStyles[i] = defaultStyle
        }
      }
    }
  }

  /**
   * Updates the link arrows based on the input data or default config value.
   */
  public updateArrows (): void {
    if (this.linksNumber === undefined) {
      this.linkArrows = undefined
      return
    }

    // Sets link arrows to default values from config if the input is missing or does not match input links number.
    const defaultArrows = this._config.linkDefaultArrows
    if (this.linkArrowsBoolean === undefined || this.linkArrowsBoolean.length !== this.linksNumber) {
      this.linkArrows = new Array(this.linksNumber).fill(+defaultArrows)
    } else {
      this.linkArrows = this.linkArrowsBoolean.map(d => +d)
    }
  }

  public updateLinkStrength (): void {
    if (this.linksNumber === undefined) {
      this.linkStrength = undefined
    }

    if (this.inputLinkStrength === undefined || this.inputLinkStrength.length !== this.linksNumber) {
      this.linkStrength = undefined
    } else {
      this.linkStrength = this.inputLinkStrength
    }
  }

  public updateClusters (): void {
    if (this.pointsNumber === undefined) {
      this.pointClusters = undefined
      this.clusterPositions = undefined
      return
    }
    if (this.inputPointClusters === undefined || this.inputPointClusters.length !== this.pointsNumber) {
      this.pointClusters = undefined
    } else {
      this.pointClusters = this.inputPointClusters
    }
    if (this.inputClusterPositions === undefined) {
      this.clusterPositions = undefined
    } else {
      this.clusterPositions = this.inputClusterPositions
    }
    if (this.inputClusterStrength === undefined || this.inputClusterStrength.length !== this.pointsNumber) {
      this.clusterStrength = undefined
    } else {
      this.clusterStrength = this.inputClusterStrength
    }
  }

  public update (): void {
    this.updatePoints()
    this.updatePointColor()
    this.updatePointSize()
    this.updatePointShape()
    this.updatePointImageIndices()
    this.updatePointImageSizes()

    this.updateLinks()
    this.updateLinkColor()
    this.updateLinkWidth()
    this.updateArrows()
    this.updateLinkStyles()
    this.updateLinkStrength()

    this.updateClusters()

    this._createAdjacencyLists()
    this._calculateDegrees()
  }

  /**
   * Returns unique point indices that are neighbors of the given point(s) —
   * i.e., connected by a link in either direction.
   * @param pointIndices - A single point index or an array of point indices.
   * @returns Array of neighboring point indices.
   */
  public getNeighboringPointIndices (pointIndices: number | number[]): number[] {
    const indices = Array.isArray(pointIndices) ? pointIndices : [pointIndices]
    const result = new Set<number>()
    for (const index of indices) {
      if (!this.isPointIndex(index)) continue
      for (const [pointIndex] of this.sourceIndexToTargetIndices?.[index] ?? []) result.add(pointIndex)
      for (const [pointIndex] of this.targetIndexToSourceIndices?.[index] ?? []) result.add(pointIndex)
    }
    return [...result]
  }

  /**
   * Returns unique link indices where both the source and target endpoints
   * are within the given point(s). Only links fully contained in the set are returned.
   * @param pointIndices - A single point index or an array of point indices.
   * @returns Array of link indices connecting points within the provided set.
   */
  public getConnectedLinkIndices (pointIndices: number | number[]): number[] {
    const indices = Array.isArray(pointIndices) ? pointIndices : [pointIndices]
    const indexSet = new Set(indices)
    const result = new Set<number>()
    for (const index of indexSet) {
      if (!this.isPointIndex(index)) continue
      for (const [targetIndex, linkIndex] of this.sourceIndexToTargetIndices?.[index] ?? []) {
        if (indexSet.has(targetIndex)) result.add(linkIndex)
      }
    }
    return [...result]
  }

  /**
   * Returns unique point indices at the endpoints (source and target) of the given link(s).
   * @param linkIndices - A single link index or an array of link indices.
   * @returns Array of point indices at the ends of the provided links.
   */
  public getConnectedPointIndices (linkIndices: number | number[]): number[] {
    const indices = Array.isArray(linkIndices) ? linkIndices : [linkIndices]
    const result = new Set<number>()
    if (this.links === undefined) return []
    const linksNumber = this.linksNumber ?? 0
    for (const linkIndex of indices) {
      // A fractional index reads one endpoint from each of two neighbouring links.
      if (!Number.isInteger(linkIndex) || linkIndex < 0 || linkIndex >= linksNumber) continue
      const sourceIndex = this.links[linkIndex * 2]
      const targetIndex = this.links[linkIndex * 2 + 1]
      // Read straight from `links`, so the endpoint check the adjacency build
      // applies has to be repeated here: a link with one invalid endpoint
      // contributes neither.
      if (!this.isPointIndex(sourceIndex) || !this.isPointIndex(targetIndex)) continue
      result.add(sourceIndex)
      result.add(targetIndex)
    }
    return [...result]
  }

  private _createAdjacencyLists (): void {
    if (this.linksNumber === undefined || this.links === undefined) {
      this.sourceIndexToTargetIndices = undefined
      this.targetIndexToSourceIndices = undefined
      return
    }

    this.sourceIndexToTargetIndices = new Array(this.pointsNumber).fill(undefined)
    this.targetIndexToSourceIndices = new Array(this.pointsNumber).fill(undefined)
    for (let i = 0; i < this.linksNumber; i++) {
      const sourceIndex = this.links[i * 2]
      const targetIndex = this.links[i * 2 + 1]
      // Both endpoints must be real points: an out-of-range index would extend
      // these arrays past the point count and come back out of
      // `getNeighboringPointIndices` as a point the caller cannot look up.
      // Skipped rather than dropped, so link indices stay the caller's own.
      if (this.isPointIndex(sourceIndex) && this.isPointIndex(targetIndex)) {
        this.sourceIndexToTargetIndices[sourceIndex] ??= []
        this.sourceIndexToTargetIndices[sourceIndex].push([targetIndex, i])

        this.targetIndexToSourceIndices[targetIndex] ??= []
        this.targetIndexToSourceIndices[targetIndex].push([sourceIndex, i])
      }
    }
  }

  private _calculateDegrees (): void {
    if (this.pointsNumber === undefined) {
      this.degree = undefined
      this.inDegree = undefined
      this.outDegree = undefined
      return
    }

    this.degree = new Array(this.pointsNumber).fill(0)
    this.inDegree = new Array(this.pointsNumber).fill(0)
    this.outDegree = new Array(this.pointsNumber).fill(0)

    for (let i = 0; i < this.pointsNumber; i++) {
      this.inDegree[i] = this.targetIndexToSourceIndices?.[i]?.length ?? 0
      this.outDegree[i] = this.sourceIndexToTargetIndices?.[i]?.length ?? 0
      this.degree[i] = (this.inDegree[i] ?? 0) + (this.outDegree[i] ?? 0)
    }
  }
}
